import { useMemo, useState, type JSX } from "react";
import { compact, DAY, LABEL, PROVIDERS, pct, useAnalytics, useModelBaseline, type Provider } from "../data.js";
import { Icon, Logo, Meter, PageHead, Pill, Seg } from "../ui.js";

const midnight = (): number => { const d = new Date(); d.setHours(0, 0, 0, 0); return Math.floor(d.getTime() / 1000); };

interface Row { model: string; today: number | null; week: number | null; share: number | null; }

const rank = (value: number | null | undefined): number => value ?? Number.NEGATIVE_INFINITY;

/** Heaviest first: 7-day delta ↓, today ↓, cumulative tokens ↓, name ↑. Unknown (null) sorts as the smallest. */
export function byUsage(a: Row & { tokens: number }, b: Row & { tokens: number }): number {
  return rank(b.week) - rank(a.week) || rank(b.today) - rank(a.today) || rank(b.tokens) - rank(a.tokens) || (a.model < b.model ? -1 : a.model > b.model ? 1 : 0);
}

export function Models({ canAdd, onConfigure, onDetail }: { canAdd: boolean; onConfigure: () => void; onDetail: (provider: Provider) => void }): JSX.Element {
  const { data, error, reload } = useAnalytics(7);
  // Window boundaries are fixed for the lifetime of the page so the baseline requests do not refire on every render.
  const [weekStart] = useState(() => Math.floor(Date.now() / 1000) - 7 * DAY);
  const [dayStart] = useState(midnight);
  const weekBase = useModelBaseline(weekStart, 12 * 3600);
  const todayBase = useModelBaseline(dayStart, 3600);
  const [filter, setFilter] = useState<"all" | Provider>("all");
  const [query, setQuery] = useState("");

  const groups = useMemo(() => PROVIDERS.filter((provider) => filter === "all" || filter === provider).map((provider) => {
    const models = data?.providers.find((item) => item.provider === provider)?.models ?? [];
    const week = weekBase[provider];
    const today = todayBase[provider];
    const deltas = models.map((item) => ({ model: item.model, tokens: item.tokens, today: today ? Math.max(0, item.tokens - (today.get(item.model) ?? 0)) : null, week: week ? Math.max(0, item.tokens - (week.get(item.model) ?? 0)) : null }));
    const weekTotal = deltas.reduce((sum, item) => sum + (item.week ?? 0), 0);
    const rows: Row[] = deltas
      .filter((item) => !query || item.model.toLowerCase().includes(query.toLowerCase()))
      .map((item) => ({ ...item, share: item.week === null ? null : weekTotal > 0 ? (item.week / weekTotal) * 100 : 0 }))
      .sort(byUsage);
    return { provider, rows };
  }), [data, weekBase, todayBase, filter, query]);

  return (
    <>
      <PageHead title="模型管理" subtitle="查看各模型的用量和状态" right={<button className="cx-btn red outline" onClick={reload}><Icon name="refresh" />刷新</button>} />
      <div className="cx-toolbar">
        <Seg items={[["all", "全部模型"], ["claude", "Claude 系列"], ["codex", "Codex 系列"]] as const} value={filter} onChange={setFilter} />
        <label className="cx-search"><Icon name="search" /><input placeholder="搜索模型..." value={query} onChange={(event) => setQuery(event.target.value)} /></label>
        {canAdd && <button className="cx-btn blue solid" onClick={onConfigure}><Icon name="plus" />添加模型</button>}
      </div>
      {error && <p className="cx-error">{error}</p>}
      <div className={`cx-grid2 cx-fill ${groups.length === 1 ? "single" : ""}`}>
        {groups.map((group) => (
          <section className={`cx-card neon ${group.provider} cx-table-card`} key={group.provider}>
            <header className="cx-card-head"><Logo provider={group.provider} size={24} /><h2>{LABEL[group.provider]}</h2></header>
            <div className="cx-mtable">
              <div className="cx-mrow head"><span>模型名称</span><span>今日 Token</span><span>7天用量</span><span className="st">状态</span><span /></div>
              <div className="cx-mbody">
                {group.rows.map((row) => (
                  <div className="cx-mrow" key={row.model}>
                    <span className="name" title={row.model}>{row.model}</span>
                    <span className="num">{row.today === null ? "—" : compact(row.today, 1)}</span>
                    <span className="share"><Meter value={row.share} provider={group.provider} /><b>{row.share === null ? "—" : pct(row.share)}</b></span>
                    <span className="st">{row.week === null ? <Pill tone="idle">未知</Pill> : row.week > 0 ? <Pill tone="ok">已连接</Pill> : <Pill tone="idle">空闲</Pill>}</span>
                    <button className="cx-more" aria-label={`${LABEL[group.provider]} 详情`} onClick={() => onDetail(group.provider)}><Icon name="more" /></button>
                  </div>
                ))}
                {!group.rows.length && <div className="cx-empty">{query ? "没有匹配的模型" : "暂无该厂商的模型明细"}</div>}
              </div>
            </div>
          </section>
        ))}
      </div>
    </>
  );
}
